import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  FolderTree,
  Info,
  Layers,
  Loader2,
  Plug,
  Puzzle,
  ShieldCheck,
  SlidersHorizontal,
  Upload,
  Sparkles,
} from "lucide-react";
import { DiffEditor } from "@monaco-editor/react";

import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Textarea } from "@/shared/components/ui/textarea";
import { useToast } from "@/shared/hooks/use-toast";
import { useTheme } from "@/shared/hooks/use-theme";
import {
  FilesStep,
  OverviewStep,
} from "@/features/Skills/components/SkillInstaller/steps";
import { parseSkillFacets } from "@/features/Skills/utils/facets";
import { textoDaSkill } from "@/features/Skills/utils/text";
import { SkillProvenance } from "@/features/Skills/components/SkillProvenance";
import { invalidarSkills } from "@/features/Skills/utils/invalidate";
import { DeclarationsSummary } from "./SkillDeclarationSteps";
import { parametrosDoPacote, requisitosDoPacote } from "../utils/skillDeclarations";
import { SkillsAdminService, type SkillAdminEntry } from "../services/skillsAdminService";

/** O arquivo que toda skill tem, e o padrão razoável para abrir a comparação. */
const ARQUIVO_DE_ENTRADA = "SKILL.md";

/**
 * O detalhe de uma skill do catálogo da plataforma (AB#85634, AC10).
 *
 * Rota própria, e não uma aba dentro de Templates: a árvore de arquivos e a
 * comparação entre versões não cabem no formato de tabela das outras abas.
 *
 * === Por que espelha a tela de configuração ===
 *
 * A pergunta "o que esta skill faz" é a mesma para quem administra o catálogo e
 * para quem a instalou, então a resposta usa os MESMOS componentes — cabeçalho,
 * Overview, License, Content. Duas telas com o mesmo propósito e layouts
 * diferentes obrigariam a pessoa a reaprender, e divergiriam na primeira
 * correção.
 *
 * O que não se repete é o que só existe numa instalação: as integrações
 * escolhidas, o histórico de quem instalou, a desinstalação. Aqui não há
 * instalação — há catálogo. Em troca aparece o que só o catálogo tem: abrir uma
 * versão que **não está no ar** e **comparar duas versões**.
 */
export function SkillAdminDetail() {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { name = "" } = useParams<{ name: string }>();

  const [versaoAberta, setVersaoAberta] = useState<number | undefined>(undefined);
  const [motivo, setMotivo] = useState("");

  /**
   * O que a PLATAFORMA decide sobre a skill — core, situação — não vem no
   * detalhe, que descreve o pacote. Vem da mesma listagem da tabela, sob a
   * mesma chave de cache: chegando pela tabela não há requisição nova.
   */
  const { data: catalogo = [] } = useQuery({
    queryKey: ["skills_admin_catalog"],
    queryFn: () => SkillsAdminService.list(),
  });

  const noCatalogo = catalogo.find((entrada) => entrada.name === name);

  /**
   * Coloca esta versão no ar.
   *
   * Serve para os dois sentidos: publicar o que foi registrado e ainda não foi
   * servido, e devolver o catálogo a uma versão anterior quando a nova deu
   * errado. É a mesma operação — o que muda é qual versão se escolhe.
   */
  /** A versão que espera confirmação para ir ao ar. */
  const [confirmando, setConfirmando] = useState<number | null>(null);

  const publicar = useMutation({
    mutationFn: (version: number) => SkillsAdminService.publishVersion(name, version),
    onSuccess: (resultado, version) => {
      invalidarSkills(queryClient);
      toast({
        title: t("skills.publish.publishedTitle"),
        description: [
          t("skills.publish.publishedDescription", { skill: name, version }),
          resultado.moved > 0 ? t("skills.publish.carried", { count: resultado.moved }) : null,
          resultado.kept > 0 ? t("skills.publish.keptBehind", { count: resultado.kept }) : null,
        ]
          .filter(Boolean)
          .join(" "),
      });
    },
    onError: (falha) => {
      toast({
        variant: "destructive",
        title: t("skills.publish.publishFailed"),
        description: falha instanceof Error ? falha.message : undefined,
      });
    },
  });

  const alterar = useMutation({
    mutationFn: (mudancas: Parameters<typeof SkillsAdminService.update>[1]) =>
      SkillsAdminService.update(name, mudancas),
    onSuccess: () => {
      invalidarSkills(queryClient);
      setMotivo("");
      toast({ title: t("skills.admin.saved") });
    },
    onError: (falha) => {
      toast({
        variant: "destructive",
        title: t("skills.admin.saveFailed"),
        description: falha instanceof Error ? falha.message : undefined,
      });
    },
  });

  const { data: versoes = [] } = useQuery({
    queryKey: ["skill_admin_versions", name],
    enabled: !!name,
    queryFn: () => SkillsAdminService.versions(name),
  });

  const { data: detalhe, isLoading } = useQuery({
    queryKey: ["skill_admin_detail", name, versaoAberta ?? "current"],
    enabled: !!name,
    queryFn: () => SkillsAdminService.detail(name, versaoAberta),
  });

  // O padrão compara a corrente com a imediatamente anterior, que é a pergunta
  // que se faz ao revisar o que acabou de ser publicado.
  const [de, setDe] = useState<number | null>(null);
  const [para, setPara] = useState<number | null>(null);

  const comparacao = useMemo(() => {
    if (versoes.length < 2) return null;

    const ordenadas = [...versoes].sort((a, b) => b.version - a.version);

    return { de: de ?? ordenadas[1]!.version, para: para ?? ordenadas[0]!.version };
  }, [versoes, de, para]);

  const { data: antes, isLoading: carregandoAntes } = useQuery({
    queryKey: ["skill_admin_diff", name, comparacao?.de, ARQUIVO_DE_ENTRADA],
    enabled: !!comparacao,
    queryFn: () => SkillsAdminService.resource(name, ARQUIVO_DE_ENTRADA, comparacao!.de),
  });

  const { data: depois, isLoading: carregandoDepois } = useQuery({
    queryKey: ["skill_admin_diff", name, comparacao?.para, ARQUIVO_DE_ENTRADA],
    enabled: !!comparacao,
    queryFn: () => SkillsAdminService.resource(name, ARQUIVO_DE_ENTRADA, comparacao!.para),
  });

  const etiquetas = useMemo(() => parseSkillFacets(detalhe?.facets ?? []), [detalhe?.facets]);

  /**
   * O que a versão aberta exige e pergunta, lido do frontmatter dela.
   *
   * Do frontmatter, e não de um campo da api: é exatamente o que a instalação
   * vai ler quando alguém instalar esta versão, e ler do mesmo lugar é o que
   * garante que as duas telas não divirjam.
   */
  const requisitos = useMemo(() => {
    if (!detalhe) return [];

    const emTextoPlano = (detalhe.metadata["required-integrations"] ?? "")
      .split(/[\s,]+/)
      .map((token) => token.trim())
      .filter(Boolean);

    return requisitosDoPacote(detalhe.frontmatter, emTextoPlano);
  }, [detalhe]);

  const parametros = useMemo(
    () => (detalhe ? parametrosDoPacote(detalhe.frontmatter) : []),
    [detalhe]
  );

  const noAr = versoes.find((versao) => versao.version === detalhe?.version)?.current === true;

  /** A versão que está no ar hoje, seja qual for a aberta no seletor. */
  const noArAgora = versoes.find((versao) => versao.current)?.version ?? null;

  /** Quantas instalações a publicação vai alcançar — vem da mesma listagem. */
  const instalacoes =
    queryClient
      .getQueryData<SkillAdminEntry[]>(["skills_admin_catalog"])
      ?.find((linha) => linha.name === name)?.installation_count ?? 0;

  if (isLoading || !detalhe) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  /** Ler pela versão ABERTA: é o que distingue esta tela da de consumo. */
  const lerArquivo = (path: string) => SkillsAdminService.resource(name, path, detalhe.version);

  return (
    <div className="w-full space-y-6 px-4">
      <Button variant="ghost" size="sm" className="gap-1 px-0" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />
        {t("skills.settings.back")}
      </Button>

      {/* Mesmo cabeçalho da tela de configuração: ícone, título com a versão ao
          lado, slug, descrição e etiquetas. */}
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Puzzle className="h-6 w-6 text-primary" />
        </div>

        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold">
              {textoDaSkill(detalhe.title, detalhe.title_i18n, i18n.language) || detalhe.name}
            </h1>
            <Badge variant="outline">{t("skills.version", { version: detalhe.version })}</Badge>
            {!noAr && (
              <Badge variant="secondary" className="text-[11px]">
                {t("skills.admin.notLive")}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{detalhe.name}</p>
          <p className="text-sm text-muted-foreground">
            {textoDaSkill(detalhe.description, detalhe.description_i18n, i18n.language)}
          </p>

          {etiquetas.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 pt-1">
              {etiquetas.map((etiqueta) => (
                <Badge key={etiqueta.raw} variant="secondary" className="text-[11px] font-normal">
                  {etiqueta.value}
                </Badge>
              ))}
            </div>
          )}

          {/* Autor, dono e fonte: o que a importação decide sobre a skill
              também se lê aqui, e não só no assistente. */}
          <SkillProvenance detail={detalhe} className="pt-1" />
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <div className="border-b">
          <TabsList>
            <TabsTrigger value="overview">
              <Info className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.overview")}
            </TabsTrigger>
            <TabsTrigger value="content">
              <FolderTree className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.content")}
            </TabsTrigger>
            <TabsTrigger value="integrations">
              <Plug className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.integrations")}
            </TabsTrigger>
            <TabsTrigger value="parameters">
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              {t("skills.parameters.title")}
            </TabsTrigger>
            <TabsTrigger value="versions">
              <Layers className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.versions")}
            </TabsTrigger>
            <TabsTrigger value="security">
              <ShieldCheck className="mr-2 h-4 w-4" />
              {t("skills.settings.tabs.security")}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* O seletor vale para todas as abas de leitura, e por isso fica acima
            delas — trocar a versão troca o que se está lendo. */}
        <div className="mt-4 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("skills.admin.showVersion")}</span>
          <Select
            value={String(detalhe.version)}
            onValueChange={(valor) => setVersaoAberta(Number(valor))}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {versoes.map((versao) => (
                <SelectItem key={versao.version} value={String(versao.version)}>
                  {t("skills.version", { version: versao.version })}
                  {versao.current ? ` · ${t("skills.settings.versions.latest")}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* A licença vem dentro da apresentação, como nas outras telas. */}
        <TabsContent value="overview" className="mt-4">
          <OverviewStep
            detail={detalhe}
            showMeta={false}
            license={{ cacheKey: ["admin", detalhe.version], readFile: lerArquivo }}
            bodyClassName="max-h-[calc(100vh-24rem)] min-h-[420px]"
          />
        </TabsContent>

        <TabsContent value="content" className="mt-4">
          <FilesStep
            detail={detalhe}
            cacheKey={["admin", detalhe.version]}
            readFile={lerArquivo}
            className="h-[calc(100vh-26rem)] min-h-[440px]"
          />
        </TabsContent>

        {/* O que ESTA versão exige e pergunta. Só leitura: versão publicada é
            imutável — mudar a declaração é publicar outra. Diferente da tela de
            configuração, onde a aba de integrações mostra a INSTÂNCIA escolhida
            na instalação; aqui não há instalação, há catálogo. */}
        <TabsContent value="integrations" className="mt-4">
          <DeclarationsSummary
            requisitos={requisitos}
            vazio={t("skills.publish.integrationsEmpty")}
          />
        </TabsContent>

        <TabsContent value="parameters" className="mt-4">
          <DeclarationsSummary
            parametros={parametros}
            vazio={t("skills.publish.parametersEmpty")}
          />
        </TabsContent>

        {/* Publicar é o único ato desta tela com efeito fora dela: leva as
            instalações junto. Por isso confirma, e diz para quantas. */}
        <AlertDialog
          open={confirmando !== null}
          onOpenChange={(aberto) => !aberto && setConfirmando(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("skills.publish.confirmTitle", { version: confirmando })}
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <span className="block">{t("skills.publish.confirmBody")}</span>

                {/* Publicar uma versão ANTERIOR à do ar é um retorno, e a tela
                    diz isso com todas as letras — a ação é a mesma, a intenção
                    não. */}
                {confirmando !== null && noArAgora !== null && confirmando < noArAgora && (
                  <span className="block">
                    {t("skills.publish.confirmRollback", { current: noArAgora })}
                  </span>
                )}

                <span className="block">
                  {instalacoes
                    ? t("skills.publish.confirmInstalls", { count: instalacoes })
                    : t("skills.publish.confirmNoInstalls")}
                </span>

                <span className="block text-muted-foreground">
                  {(() => {
                    const alvo = versoes.find((versao) => versao.version === confirmando);

                    return (
                      textoDaSkill(alvo?.changelog, alvo?.changelog_i18n, i18n.language) ||
                      t("skills.publish.noChangelog")
                    );
                  })()}
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (confirmando !== null) publicar.mutate(confirmando);
                  setConfirmando(null);
                }}
              >
                {t("skills.publish.publishLatest")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <TabsContent value="versions" className="mt-4 space-y-4">
          <div className="space-y-2">
            {versoes.map((versao) => (
              <div
                key={versao.version}
                className="flex items-start justify-between gap-4 rounded-md border p-4"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium">
                      {t("skills.version", { version: versao.version })}
                    </p>
                    {/* Três estados, não dois: a que está no ar, a que foi
                        registrada e nunca chegou lá, e as que já saíram. Sem
                        isso, uma versão esperando publicação era indistinguível
                        de uma v1 antiga. */}
                    {versao.current ? (
                      <Badge variant="secondary" className="text-[11px]">
                        {t("skills.publish.onAir")}
                      </Badge>
                    ) : versao.version > (noArAgora ?? 0) ? (
                      <Badge variant="outline" className="text-[11px]">
                        {t("skills.publish.notPublished")}
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-[11px] font-normal text-muted-foreground"
                      >
                        {t("skills.publish.offAir")}
                      </Badge>
                    )}
                  </div>
                  {textoDaSkill(versao.changelog, versao.changelog_i18n, i18n.language) && (
                    <p className="text-sm text-muted-foreground">
                      {textoDaSkill(versao.changelog, versao.changelog_i18n, i18n.language)}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <span className="text-xs text-muted-foreground">
                    {new Date(versao.published_at).toLocaleDateString()}
                  </span>

                  {!versao.current && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={publicar.isPending}
                      onClick={() => setConfirmando(versao.version)}
                    >
                      <Upload className="mr-1 h-3.5 w-3.5" />
                      {t("skills.publish.publishVersion")}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-3 rounded-md border p-4">
            <p className="flex items-center gap-1.5 text-sm font-medium">
              <Sparkles className="h-4 w-4" />
              {t("skills.admin.compare")}
            </p>

            {!comparacao ? (
              <p className="text-sm text-muted-foreground">{t("skills.admin.needTwoVersions")}</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={String(comparacao.de)} onValueChange={(v) => setDe(Number(v))}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {versoes.map((versao) => (
                        <SelectItem key={versao.version} value={String(versao.version)}>
                          {t("skills.version", { version: versao.version })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <ArrowRight className="h-4 w-4 text-muted-foreground" />

                  <Select value={String(comparacao.para)} onValueChange={(v) => setPara(Number(v))}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {versoes.map((versao) => (
                        <SelectItem key={versao.version} value={String(versao.version)}>
                          {t("skills.version", { version: versao.version })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <span className="text-xs text-muted-foreground">
                    {t("skills.admin.comparingFile", { path: ARQUIVO_DE_ENTRADA })}
                  </span>
                </div>

                <div className="h-[480px] overflow-hidden rounded-md border">
                  {carregandoAntes || carregandoDepois ? (
                    <div className="p-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    // Somente leitura nos dois lados: versão publicada é
                    // imutável, e editar aqui prometeria algo que não acontece.
                    <DiffEditor
                      height="100%"
                      language="markdown"
                      original={antes?.content ?? ""}
                      modified={depois?.content ?? ""}
                      theme={theme === "dark" ? "vs-dark" : "vs"}
                      options={{
                        readOnly: true,
                        renderSideBySide: true,
                        minimap: { enabled: false },
                        wordWrap: "on",
                        fontSize: 13,
                        scrollBeyondLastLine: false,
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="security" className="mt-4 space-y-4">
          {/* Core primeiro, porque é a decisão de maior alcance: ela não é
              sobre esta organização, é sobre TODAS. */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4" />
                {t("skills.admin.core")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">{t("skills.admin.coreHint")}</p>

              {noCatalogo?.is_core ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={alterar.isPending}
                  onClick={() => alterar.mutate({ is_core: false })}
                >
                  {t("skills.admin.removeCore")}
                </Button>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t("skills.admin.corePromptBody", {
                      skill: detalhe.title || detalhe.name,
                    })}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={alterar.isPending}
                    onClick={() => alterar.mutate({ is_core: true })}
                  >
                    {t("skills.admin.makeCore")}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Inativar, e não excluir (AC11): quem já instalou encontra a skill
              recusada COM o motivo, em vez de ela sumir. Excluir cascatearia
              para `skill_base` e apagaria a instalação das pessoas em
              silêncio — por isso não existe aqui. */}
          <Card className="border-destructive/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-destructive">
                {t("skills.admin.dangerZone")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {noCatalogo?.status === "inactive" ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t("skills.admin.inactiveNow", {
                      reason: noCatalogo.inactive_reason || t("skills.admin.noReason"),
                    })}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={alterar.isPending}
                    onClick={() => alterar.mutate({ status: "active" })}
                  >
                    {t("skills.admin.reactivate")}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    {t("skills.admin.inactivateHint", {
                      count: noCatalogo?.installation_count ?? 0,
                    })}
                  </p>
                  <Textarea
                    value={motivo}
                    onChange={(evento) => setMotivo(evento.target.value)}
                    rows={2}
                    placeholder={t("skills.admin.reasonPlaceholder")}
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={alterar.isPending || !motivo.trim()}
                    onClick={() =>
                      alterar.mutate({ status: "inactive", inactive_reason: motivo.trim() })
                    }
                  >
                    {t("skills.admin.inactivate")}
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    {t("skills.admin.reasonRequired")}
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
